import { type WishlistDTO, type WishlistEntryDTO } from "~/features/wishlist/types/dto";
import { type ProductData } from "~/domains/product/types/ProductData";
import { type WishlistEntryModel, type WishlistModel } from "~/features/wishlist/types/model";
import { transformProduct } from "~/features/productDetails/transformers/ProductDataTransformer";
import { resolveApiUrl } from "~/utils/url/urlResolver";

const apiUrl = resolveApiUrl("/wishlists");

export type FetchWishlistsResponse = {
  wishlists: WishlistModel[];
  wishlistEntries: WishlistEntryModel[];
  products: ProductData[];
};
export async function fetchWishlists(): Promise<FetchWishlistsResponse> {
  const result = await fetch(apiUrl, {
    credentials: "same-origin", // include, omit
    mode: "cors", // no-cors, same-origin
    referrer: "no-referrer", // client
  });

  if (!result.ok) {
    throw new Error("" + result.status);
  }

  const json = (await result.json()) as WishlistDTO[];

  return transformResponse(json);
}

const transformResponse = (data: WishlistDTO[]): FetchWishlistsResponse => {
  const allEntries = data.map((wishlist) => wishlist.entries ?? []).flat();

  return {
    wishlists: data.map(transformWishlist),
    wishlistEntries: allEntries.map(transformEntry),
    products: [
      ...allEntries.map((entry) => entry.product).map((x) => transformProduct(x)),
      ...allEntries
        .flatMap((entry) => entry.optionalParts ?? [])
        .map((part) => part.product)
        .map((x) => transformProduct(x)),
    ],
  };
};

export const transformWishlist = (dto: WishlistDTO): WishlistModel => ({
  id: dto.id,
  name: dto.name ?? "",
  comment: dto.comment ?? "",
  entryIds: dto.entries?.map((entry) => entry.id) ?? [],
  lastModification: dto.modifiedtime,
});

export const transformEntry = (dto: WishlistEntryDTO): WishlistEntryModel => ({
  id: dto.id,
  productCode: dto.product.code,
  quantity: dto.quantity ?? 0,
  comment: dto.comment ?? "",
  priceReference: dto.priceReference ?? "",
  available: dto.visible,

  optionalParts: (dto.optionalParts ?? []).map((part) => ({
    productCode: part.product.code,
    quantity: part.quantity,
  })),
  wishlistEntryNumber: dto.wishlistEntryNumber,
});
